import { useState, useRef, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { classNames } from '@/utils/class-names';
import { CalendarIcon } from '@heroicons/react/24/outline';

interface DateRangePickerProps {
  label?: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  maxDate?: Dayjs;
}

const DateRangePicker = ({
  label,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  maxDate,
}: DateRangePickerProps) => {
  const [isSelectingStartDate, setIsSelectingStartDate] = useState(true);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const ref = useRef<HTMLDivElement>(null);

  // Close popover if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  const handleDateClick = (day: Dayjs) => {
    // Check if date is after maxDate
    if (maxDate && day.isAfter(maxDate)) {
      return;
    }

    if (isSelectingStartDate) {
      onStartDateChange(day);
      setIsSelectingStartDate(false);
    } else {
      if (startDate && day.isBefore(startDate)) {
        onEndDateChange(null);
        onStartDateChange(day);
        setIsSelectingStartDate(false);
      } else {
        onEndDateChange(day);
        setIsPopoverOpen(false);
      }
    }
  };

  const renderDays = () => {
    const startOfMonth = currentMonth.startOf('month');
    const daysInMonth = currentMonth.daysInMonth();
    const today = dayjs();
    const firstDayOfMonth = startOfMonth.day();
    const days = [];

    // Add empty slots for days before the first day of the current month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="size-[32px]"></div>);
    }

    // Add actual days of the current month
    for (let i = 0; i < daysInMonth; i++) {
      const day = startOfMonth.add(i, 'day');
      const isSelectedStart = startDate && day.isSame(startDate, 'day');
      const isSelectedEnd = endDate && day.isSame(endDate, 'day');
      const isInRange = startDate && endDate && day.isAfter(startDate, 'day') && day.isBefore(endDate, 'day');
      const isDisabled = maxDate && day.isAfter(maxDate);

      days.push(
        <motion.button
          key={i}
          type="button"
          disabled={isDisabled}
          className={classNames(
            'mt-1 size-[32px]',
            isSelectedStart || isSelectedEnd ? 'bg-[#CEE0F5] font-bold text-white' : isInRange ? 'bg-[#CEE0F5]' : '',
            isSelectedStart ? 'rounded-l-full' : '',
            isSelectedEnd ? 'rounded-r-full' : '',
            startDate && !endDate && isSelectedStart ? '!bg-white' : '',
            !startDate && endDate && isSelectedEnd ? '!bg-white' : '',
            day.isSame(today, 'day') && !day.isSame(startDate) && !isInRange ? 'rounded-full border border-black' : '',
            isSaturday(day) || isLastDayOfMonth(day) ? 'rounded-r-full' : '',
            isSunday(day) || isFirstDayOfMonth(day) ? 'rounded-l-full' : '',
            isDisabled ? 'cursor-not-allowed opacity-50' : '',
          )}
          onClick={() => handleDateClick(day)}>
          <div
            className={classNames(
              isSelectedStart || isSelectedEnd ? 'size-[32px] rounded-full bg-[#1775E3]' : '',
              'flex items-center justify-center text-center text-sh-xxs font-medium',
            )}>
            {day.format('D')}
          </div>
        </motion.button>,
      );
    }

    return days;
  };

  const isSaturday = (day: Dayjs): boolean => day.day() === 6;
  const isSunday = (day: Dayjs): boolean => day.day() === 0;
  const isFirstDayOfMonth = (day: Dayjs): boolean => day.date() === 1;
  const isLastDayOfMonth = (day: Dayjs): boolean => day.date() === day.daysInMonth();

  const handlePreviousMonth = () => setCurrentMonth(currentMonth.subtract(1, 'month'));
  const handleNextMonth = () => {
    // Don't allow going to future months beyond maxDate
    if (maxDate && currentMonth.add(1, 'month').startOf('month').isAfter(maxDate)) {
      return;
    }
    setCurrentMonth(currentMonth.add(1, 'month'));
  };

  const openStartDatePicker = () => {
    setIsSelectingStartDate(true);
    setIsPopoverOpen(true);
  };

  const openEndDatePicker = () => {
    setIsSelectingStartDate(false);
    setIsPopoverOpen(true);
  };

  return (
    <div className="flex flex-col items-start">
      <p className="text-sh-xxs font-medium text-[#263238]">{label}</p>
      <div className="relative my-2 flex items-center justify-center">
        <div className="relative mr-2 flex-1">
          <div className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 transform">
            <CalendarIcon className="size-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="flex w-full cursor-pointer items-center justify-center rounded-xl border border-[#CFD8DC] px-3 py-4 pl-10 text-sh-xxs"
            value={startDate ? startDate.format('DD/MM/YYYY') : ''}
            onClick={openStartDatePicker}
            readOnly
            placeholder="Start Date"
          />
        </div>

        <span className="pr-2 text-sh-xxs text-[#263238]">to</span>

        <div className="relative flex-1">
          <div className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 transform">
            <CalendarIcon className="size-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full cursor-pointer rounded-xl border border-[#CFD8DC] px-3 py-4 pl-10 text-sh-xxs"
            value={endDate ? endDate.format('DD/MM/YYYY') : ''}
            onClick={openEndDatePicker}
            readOnly
            placeholder="End Date"
          />
        </div>

        <AnimatePresence>
          {isPopoverOpen && (
            <motion.div
              ref={ref}
              className="absolute left-0 top-12 z-50 rounded border border-gray-200 bg-white p-4 shadow-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}>
              <div className="mb-4 flex items-center justify-between">
                <motion.button
                  type="button"
                  onClick={handlePreviousMonth}
                  whileTap={{ scale: 0.9 }}
                  className="rounded p-1 hover:bg-gray-200">
                  <ChevronLeftIcon className="h-5 w-5" />
                </motion.button>
                <span className="text-sh-xss font-medium text-[#00000099]">{currentMonth.format('MMMM YYYY')}</span>
                <motion.button
                  type="button"
                  onClick={handleNextMonth}
                  whileTap={{ scale: 0.9 }}
                  className="rounded p-1 hover:bg-gray-200">
                  <ChevronRightIcon className="h-5 w-5" />
                </motion.button>
              </div>

              <div className="mb-2 grid grid-cols-7">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                  <div key={index} className="size-[32px] text-center text-sh-xss font-medium text-[#00000099]">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">{renderDays()}</div>
              <div className="mt-2 flex justify-between">
                <span className="text-sh-xxs text-gray-600">
                  {isSelectingStartDate ? 'Selecting start date' : 'Selecting end date'}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DateRangePicker;
